import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { SubscriptionsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { Content } from './styles/Content';
import { RangeSliderWrapper } from './styles/RangeSliderWrapper';

type TProps = {
  billingAccountId: string;
  subscriptionProductId: string;
  planName: string;
  price: number;
  freeSeats: number;
  onSuccess: (id?: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialNumberOfLicenses: number;
};

export const ConfirmSubscription = ({
  billingAccountId,
  subscriptionProductId,
  planName,
  price,
  freeSeats,
  onCancel,
  onSuccess,
  initialNumberOfLicenses = 1,
}: TProps) => {
  const { call } = useAPI();
  const { updateMe } = useAuthContext();
  const { openModal } = useModalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [numberOfLicenses, setNumberOfLicenses] = useState(initialNumberOfLicenses);

  const handleError = (message?: string, result?: { error?: string }) => {
    let errorMessage = message ?? result?.error ?? 'Something went wrong. Please contact administrator.';
    if (result?.error === 'No payment methods found') {
      errorMessage = 'Cannot create subscription. Please add a payment method first.';
    }
    if (result?.error === 'Subscription already exists') {
      errorMessage = 'You already have this subscription. Please choose another or modify an existing subscription.';
    }
    onCancel();
    openModal(ModalNamesEnum.GenericErrorModal, {
      message: errorMessage,
    });
  };

  const innerSubmit = async () => {
    setIsSubmitting(true);
    let requestErrorMessage: string | undefined;
    try {
      const result = await call(
        () =>
          SubscriptionsService.create({
            baId: billingAccountId,
            requestBody: { subscriptionProductId: subscriptionProductId, licenseCount: numberOfLicenses },
          }),
        {
          onError: (message) => {
            if (typeof message === 'string') {
              requestErrorMessage = message;
            }
          },
        },
      );
      if (!result || !result.success) {
        handleError(requestErrorMessage, result);
        return;
      }
      updateMe();
      onSuccess(result.id);
    } catch {
      handleError(requestErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper width="480px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle data-testid="purchasePlan">Confirm</ModalTitle>
      <Content>
        <p className="description">
          You are about to purchase the &quot;{planName}&quot; plan for ${price.toFixed(2)} per license per month +
          applicable sales tax
        </p>

        <RangeSliderWrapper>
          <label htmlFor="steps-range" className="mutedLabel block mb-2 font-[600] text-xs text-left">
            Number of licenses:
          </label>
          <input
            id="steps-range"
            type="range"
            min={1}
            max={250}
            value={numberOfLicenses}
            step={1}
            onChange={(e) => setNumberOfLicenses(Number(e.target.value))}
            className="w-full h-2 bg-[var(--color-surface-muted)] rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">
            {numberOfLicenses} license{numberOfLicenses > 1 && 's'}
          </span>
        </RangeSliderWrapper>

        <div className="text-left my-4 flex flex-col">
          {freeSeats ? (
            <div>
              <span className="mutedLabel font-[600] text-xs">Free licenses in subscription plan:</span>
              <span className="float-right">{freeSeats}</span>
            </div>
          ) : null}
          <div>
            <span className="mutedLabel font-[600] text-xs">Total subscription price per month:</span>
            <span className="float-right">
              ${numberOfLicenses <= freeSeats ? 0 : (Number(price) * (numberOfLicenses - freeSeats)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="checkbox-wrapper">
          <CheckBox
            name="terms-accepted"
            checked={termsAccepted}
            onChange={(value) => setTermsAccepted(value)}
            testId="accept-terms-checkbox"
          />
          <label>
            I agree to the{' '}
            <a target="_blank" rel="noreferrer" href="/agreement.html">
              Terms of Service
            </a>
          </label>
        </div>

        <div className="buttonContainer">
          <OutlinedButton className="w-full" type="button" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <Button
            className="w-full"
            data-testid="submitButton"
            type="submit"
            onClick={() => innerSubmit()}
            disabled={isSubmitting || !termsAccepted}
          >
            Purchase
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
